import { FileData } from "../../FileData";
import { BaseDataDefinition } from "./BaseDataDefinition";
import { DataTypes } from "../DataTypes";
import { ValidationIssue } from "../../IDesignValidationProvider";
import { ExportShemaFileType, JSONDummyData } from "../Constants/DataDefinitionConstants";
import type { AppGlueAny } from "../../AppGlueAny";
import { AppGlueError } from "../../../AppGlueError";
import { DataUtilities } from "../../DataUtilities";

export class FileDataDefinition extends BaseDataDefinition<FileData> {
    readonly type: DataTypes = DataTypes.FILE;

    // we cannot convert into a file, it either is or is not.

    // detect schema is for file
    static isFileSchema(obj: object): boolean {
        let type = Reflect.get(obj, "type") as string;

        if (type !== ExportShemaFileType) {
            return false;
        }

        return true;
    }

    static isValidJSONObject(obj: object): boolean {
        if (typeof obj !== "object") return false;
        let fileName = Reflect.get(obj, "fileName");
        if (!fileName) return false;

        let fileContents = Reflect.get(obj, "fileContents");
        if (!fileContents) return false;

        return true;
    }

    static fromJSONObjectValue(value: AppGlueAny, name: string): FileDataDefinition | undefined {
        if (Array.isArray(value)) {
            if (!value.find((v) => !FileDataDefinition.isValidJSONObject(v))) {
                let def = new FileDataDefinition(name);
                def.list = true;

                return def;
            }
        } else if (FileDataDefinition.isValidJSONObject(value)) {
            let def = new FileDataDefinition(name);
            return def;
        }

        return undefined;
    }

    static fromJSONSchema(obj: object, name?: string): FileDataDefinition | undefined {
        let def = new FileDataDefinition(name);

        let type = Reflect.get(obj, "type");
        if (type === "array") {
            def.list = true;
            let items = Reflect.get(obj, "items");

            type = Reflect.get(items, "type");

            if (type === ExportShemaFileType) {
                return def;
            }
        } else if (type === ExportShemaFileType) {
            return def;
        }

        return undefined;
    }

    // eslint-disable-next-line
    convert(_value: AppGlueAny): AppGlueAny {
        return undefined;
    }

    verifyValueType(value: AppGlueAny): ValidationIssue | undefined {
        if (value === undefined) return new ValidationIssue("Undefined Value");

        if (this.list) {
            if (!Array.isArray(value)) {
                return new ValidationIssue("is not an array of file");
            }

            for (let f of value) {
                if (!this.isFile(f)) {
                    return new ValidationIssue("array contains a non file object");
                }
            }
        } else {
            if (Array.isArray(value)) {
                return new ValidationIssue("is an array, expected single file");
            }

            if (!this.isFile(value)) {
                return new ValidationIssue("is not a file object");
            }
        }
    }

    isFile(v: AppGlueAny): boolean {
        return v instanceof FileData;
        // if (typeof v === "object") {
        //     return (
        //         (v.file && v.file instanceof File) ||
        //         (v.fileUrl &&
        //             /^(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/.test(
        //                 v.fileUrl
        //             )) ||
        //         (v.fileUrl && v.fileUrl.trim().startsWith("data:image/"))
        //     );
        // }
        // return false;
    }

    toJSONSchema(): object {
        let obj = {};

        if (this.list) {
            Reflect.set(obj, "type", "array");
            Reflect.set(obj, "items", {
                type: ExportShemaFileType,
            });
        } else {
            Reflect.set(obj, "type", ExportShemaFileType);
        }
        return obj;
    }

    toJSONObject(value: AppGlueAny): AppGlueAny {
        let issue = this.verifyValueType(value);

        // TODO: if not a file, retutn dummy data (is this correct?)
        if (issue) {
            value = new FileData(
                JSONDummyData.FILE_NAME,
                DataUtilities.stringToArrayBuffer(JSONDummyData.FILE_CONTENT),
            );
            value.fileName = JSONDummyData.FILE_NAME;

            if (this.list) {
                value = [value];
            }
        }

        // handle if it is an array of FileData
        if (Array.isArray(value)) {
            return value.map((v) => {
                this.toJSONObject(v);
            });
        }

        if (value instanceof FileData) {
            return {
                fileName: value.fileName,
                fileContents: value.data,
            };
        } else {
            throw new AppGlueError("FileDataDefinition.toJSONObject: value is not a FileData");
        }
    }
}
